import React, { useState } from "react";
import api from "../api";
import { motion, AnimatePresence } from "framer-motion";

const RSVPForm = ({ eventId, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Status Select, 2: Details Form
  const [status, setStatus] = useState(null); // confirmed | declined | pending
  const [guestName, setGuestName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const statuses = [
    { key: "confirmed", label: "سأحضر 🎉", color: "#4ade80", icon: "✅" },
    { key: "declined", label: "لن أحضر ❌", color: "#f87171", icon: "✖️" },
    { key: "pending", label: "ربما ⏳", color: "#fbbf24", icon: "⏳" },
  ];

  const handleSubmit = async () => {
    if (!guestName.trim()) {
      setError("يرجى إدخال اسمك الكريم");
      return;
    }
    if (status === "confirmed" && !phone.trim()) {
      setError("يرجى إدخال رقم هاتفك للتواصل");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await api.post(`/api/events/${eventId}/rsvp`, {
        guestName,
        phone,
        status,
        note,
      });
      localStorage.setItem(`youmak_rsvp_${eventId}`, "1");
      if (guestName) localStorage.setItem("youmak_guest_name", guestName);
      setDone(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError("عفواً، حدث خطأ أثناء الحفظ. حاول مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        className="rsvp-pro-success"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        dir="rtl"
      >
        <div className="success-icon">✨</div>
        <h3>شكراً لك!</h3>
        <p>تم تسجيل ردك بنجاح. نتطلع لرؤيتك!</p>
      </motion.div>
    );
  }

  return (
    <div className="rsvp-pro-container" dir="rtl">
      <style>{`
        .rsvp-pro-container {
          background: #fff;
          border: 1px solid #ede0d4;
          border-radius: 24px;
          padding: 2rem;
          max-width: 500px;
          margin: 0 auto;
          box-shadow: 0 10px 30px rgba(139, 73, 62, 0.05);
          font-family: inherit;
        }
        .rsvp-title {
          font-size: 1.5rem;
          font-weight: 900;
          color: #2d1e17;
          margin-bottom: 0.5rem;
          text-align: center;
        }
        .rsvp-subtitle {
          font-size: 0.95rem;
          color: #867370;
          margin-bottom: 2rem;
          text-align: center;
        }
        .status-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
        }
        .status-btn {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.25rem 1.5rem;
          border: 2px solid #f5ebe0;
          border-radius: 16px;
          background: #fff;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 1.1rem;
          font-weight: 800;
          color: #534340;
        }
        .status-btn:hover {
          border-color: #8b493e;
          background: #fdfbf7;
        }
        .status-btn.active {
          border-color: #8b493e;
          background: #fff8f5;
          color: #8b493e;
        }
        .status-icon { font-size: 1.25rem; }

        .details-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .form-label {
          font-size: 0.85rem;
          font-weight: 700;
          color: #8b493e;
        }
        .form-input {
          border: 1.5px solid #ffdad4;
          border-radius: 12px;
          padding: 0.85rem 1rem;
          font-size: 1rem;
          outline: none;
          transition: border 0.2s;
        }
        .form-input:focus { border-color: #8b493e; }

        .rsvp-actions {
          display: flex;
          gap: 1rem;
          margin-top: 1.5rem;
        }
        .btn-submit {
          flex: 2;
          background: #8b493e;
          color: #fff;
          border: none;
          border-radius: 999px;
          padding: 1rem;
          font-size: 1.1rem;
          font-weight: 800;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(139, 73, 62, 0.2);
        }
        .btn-back {
          flex: 1;
          background: #f5ebe0;
          color: #534340;
          border: none;
          border-radius: 999px;
          padding: 1rem;
          font-size: 1rem;
          font-weight: 700;
          cursor: pointer;
        }
        .error-msg {
          color: #ba1a1a;
          font-size: 0.85rem;
          font-weight: 600;
          text-align: center;
          margin-top: 0.5rem;
        }
        .rsvp-pro-success {
          text-align: center;
          padding: 3rem 2rem;
          background: #fff;
          border-radius: 24px;
          border: 1px solid #ede0d4;
        }
        .success-icon { font-size: 3rem; margin-bottom: 1rem; }
      `}</style>

      <h2 className="rsvp-title">قائمة الحضور 👥</h2>
      <p className="rsvp-subtitle">أعلمنا إذا كنت ستشاركنا الفرحة</p>

      <AnimatePresence mode="wait">
        {step === 1 ? (
          <motion.div
            key="step1"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -20, opacity: 0 }}
            className="status-grid"
          >
            {statuses.map((s) => (
              <button
                key={s.key}
                className={`status-btn ${status === s.key ? "active" : ""}`}
                onClick={() => {
                  setStatus(s.key);
                  setStep(2);
                }}
              >
                <span>{s.label}</span>
                <span className="status-icon">{s.icon}</span>
              </button>
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="step2"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -20, opacity: 0 }}
            className="details-form"
          >
            <div className="form-group">
              <label className="form-label">الاسم بالكامل *</label>
              <input
                className="form-input"
                placeholder="ادخل اسمك..."
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
              />
            </div>

            {status === "confirmed" && (
              <motion.div
                className="form-group"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
              >
                <label className="form-label">رقم الهاتف (واتساب) *</label>
                <input
                  className="form-input"
                  dir="ltr"
                  placeholder="01xxxxxxxxx"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </motion.div>
            )}

            <div className="form-group">
              <label className="form-label">ملاحظات (اختياري)</label>
              <textarea
                className="form-input"
                style={{ borderRadius: "16px", minHeight: "80px" }}
                placeholder="أية رسالة لصاحب المناسبة؟"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            {error && <p className="error-msg">{error}</p>}

            <div className="rsvp-actions">
              <button className="btn-back" onClick={() => setStep(1)}>
                رجوع
              </button>
              <button
                className="btn-submit"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "جاري الإرسال..." : "إرسال الرد ✨"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RSVPForm;
